/**
 * Firm plans: billed per active client per month.
 *
 * A firm (an organization that collects documents from clients) pays for each
 * client it worked with in a billing month, not for a page tier. The page
 * allowance is a pool: every active client adds the same number of pages, and
 * the whole firm draws from it. Pages past the pool are billed as overage.
 *
 * The client prices are a launch price for the first firms, set by Sean on
 * 2026-09-27 while LedgerBox proves the plan with early users. The W8a
 * research (`.local/firm-experience/pricing/research.md`, 2026-09-25)
 * recommended $20 a client ($16 on the term); the page numbers are still its
 * numbers. These values are the single source for the API, the web app, the
 * pricing page and the Stripe setup script, so a change here is a change
 * everywhere, including the Stripe prices the script creates.
 *
 * This module imports only types from `billing.ts`. `billing.ts` imports the
 * values below, and a value import back would be a module cycle.
 */
import type { SubscriptionStatus } from "./billing.js"

/** The firm plan family. FIRM_FREE never has a Stripe subscription. */
export const firmPlanIds = ["FIRM_FREE", "FIRM"] as const

export type FirmPlanId = (typeof firmPlanIds)[number]

export function isFirmPlanId(planId: string): planId is FirmPlanId {
  return (firmPlanIds as readonly string[]).includes(planId)
}

export const firmPricing = {
  /** Price per active client per month, month to month. */
  perActiveClientMonthlyUsd: 9,
  /** Price per active client per month on a 12-month term. Still billed
   * monthly on the meter, not prepaid. */
  termPerActiveClientMonthlyUsd: 7,
  termMonths: 12,
  /** Pages each active client adds to the firm's shared pool. */
  includedPagesPerActiveClient: 50,
  /** Price of one page past the pool, in cents. */
  overagePerPageCents: 20,
  /** Active clients the free tier covers in a billing month. */
  freeActiveClients: 3,
  /** Pages the free tier covers in a billing month, for the whole firm. */
  freePageCap: 30,
} as const

/** "$0.20" from 20: a price in US cents, as the plans state it. */
export const formatUsdCents = (cents: number): string =>
  `$${(cents / 100).toFixed(2)}`

/** Whole-number percentage the 12-month term saves. */
export const firmTermDiscountPct = Math.round(
  (1 -
    firmPricing.termPerActiveClientMonthlyUsd /
      firmPricing.perActiveClientMonthlyUsd) *
    100
)

/**
 * What makes a client active in a billing month. Any one is enough. A request
 * counts only on its first send: reminders never do, or a request that nobody
 * answers would bill the firm every month it keeps reminding.
 */
export const activeClientSignals = [
  "extraction",
  "portal_upload",
  "request_sent",
] as const

export type ActiveClientSignal = (typeof activeClientSignals)[number]

/** When each signal first happened inside the billing month; null = never. */
export type ActiveClientEvidence = {
  firstExtractionAt: Date | null
  firstPortalUploadAt: Date | null
  firstRequestSentAt: Date | null
}

/** A workspace as far as the policy needs to see it. */
export type ActiveClientCandidate = {
  clientSince: Date | null
  archivedAt: Date | null
}

export const ActiveClientPolicy = {
  signals: activeClientSignals,

  /**
   * Only client workspaces can be active clients. The firm's own books
   * (`clientSince` null) never count, and neither does an archived client.
   */
  countsWorkspace(workspace: ActiveClientCandidate): boolean {
    return workspace.clientSince !== null && workspace.archivedAt === null
  },

  /** The signals that make this client active, in a stable order. */
  reasons(evidence: ActiveClientEvidence): ActiveClientSignal[] {
    const reasons: ActiveClientSignal[] = []
    if (evidence.firstExtractionAt) reasons.push("extraction")
    if (evidence.firstPortalUploadAt) reasons.push("portal_upload")
    if (evidence.firstRequestSentAt) reasons.push("request_sent")
    return reasons
  },

  isActive(evidence: ActiveClientEvidence): boolean {
    return ActiveClientPolicy.reasons(evidence).length > 0
  },

  /** The moment the client became active: the earliest signal. */
  activeSince(evidence: ActiveClientEvidence): Date | null {
    const times = [
      evidence.firstExtractionAt,
      evidence.firstPortalUploadAt,
      evidence.firstRequestSentAt,
    ].filter((value): value is Date => value !== null)

    if (times.length === 0) return null

    return new Date(Math.min(...times.map((value) => value.getTime())))
  },
} as const

/**
 * The firm's page allowance for a billing month, before any operator grant.
 * The free tier has a fixed cap for the whole firm; the paid plan pools the
 * pages of every active client.
 */
export function firmPagePool(
  planId: FirmPlanId,
  activeClients: number
): number {
  if (planId === "FIRM_FREE") {
    return firmPricing.freePageCap
  }

  return (
    Math.max(0, Math.floor(activeClients)) *
    firmPricing.includedPagesPerActiveClient
  )
}

/** Pages past the pool. Never negative. */
export function firmOveragePages(pagesUsed: number, pool: number): number {
  return Math.max(0, pagesUsed - pool)
}

export type EffectivePlanInput<TPlanId extends string> = {
  planId: TPlanId
  status: SubscriptionStatus
  trialEndsAt: Date | null
  /** Whether the trial was unlocked (shared or upgraded). */
  trialUnlocked: boolean
  /** The organization collects documents from clients (onboarding said
   * "A firm with clients"). */
  isFirm: boolean
  now: Date
}

export type EffectivePlan<TPlanId extends string> = {
  planId: TPlanId | FirmPlanId
  status: SubscriptionStatus
  /** True when the stored row was read as the firm free tier. */
  movedToFirmFree: boolean
}

/**
 * The plan an organization is actually on, which is not always the stored
 * row.
 *
 * - A paid page plan (active or past due) is kept as it is. Firms that paid
 *   before firm plans existed are grandfathered and see no change.
 * - A firm on a locked trial is on the free tier. The trial gave it 10 pages
 *   for 14 days; the free tier gives it 30 pages every month, so nothing is
 *   taken away.
 * - A firm on an unlocked trial keeps the trial until it ends. It was promised
 *   200 pages, and taking them back mid-trial breaks that promise. When the
 *   trial ends it lands on the free tier instead of being locked out.
 * - A firm with a canceled subscription, of either family, is on the free
 *   tier: free forever means free after leaving, too.
 * - Organizations that are not firms keep their page plan and trial as before.
 *
 * Nothing writes FIRM_FREE to the database. Only Stripe sync writes a plan,
 * and the free tier has no Stripe subscription, so reading it here keeps one
 * writer.
 */
export function resolveEffectivePlan<TPlanId extends string>(
  input: EffectivePlanInput<TPlanId>
): EffectivePlan<TPlanId> {
  const toFree = {
    planId: "FIRM_FREE" as const,
    status: "active" as const,
    movedToFirmFree: true,
  }

  if (input.planId === "FIRM_FREE") {
    return { ...toFree, movedToFirmFree: false }
  }

  if (input.planId === "FIRM") {
    return input.status === "canceled"
      ? toFree
      : { planId: input.planId, status: input.status, movedToFirmFree: false }
  }

  if (!input.isFirm) {
    return {
      planId: input.planId,
      status: input.status,
      movedToFirmFree: false,
    }
  }

  if (input.status === "canceled") {
    return toFree
  }

  if (input.status === "trialing") {
    const trialOver =
      input.trialEndsAt !== null &&
      input.now.getTime() > input.trialEndsAt.getTime()

    if (trialOver || !input.trialUnlocked) {
      return toFree
    }
  }

  return { planId: input.planId, status: input.status, movedToFirmFree: false }
}

export type PlanFamily = "page" | "firm"

/**
 * Why a paid tool (forwarding, webhooks, posting) is off, for the plan
 * family in force. Every firm plan includes them, the free one too, so a
 * firm is locked out only while its Firm plan payment is due. On the page
 * plans they start at Pro. Never name Pro to a firm. `tool` is singular
 * ("Forwarding", "Webhook delivery").
 */
export const lockedToolLine = (
  planFamily: PlanFamily | undefined,
  tool: string
): string =>
  planFamily === "firm"
    ? `${tool} is off until your Firm plan payment goes through`
    : `${tool} comes with the Pro plan`

/** What to do about a locked tool, for the same plan family. */
export const lockedToolNextStep = (
  planFamily: PlanFamily | undefined
): string =>
  planFamily === "firm"
    ? "Update your payment in Settings > Billing"
    : "Upgrade in Settings > Billing"

/**
 * The whole message an API error or a failed step shows for a locked tool:
 * why, then what to do, then (optionally) what to do after that. `null`
 * means the plan family could not be read; the message then names no plan,
 * because naming Pro to a firm would be false.
 */
export const lockedToolMessage = (
  planFamily: PlanFamily | null,
  tool: string,
  then?: string
): string => {
  const after = then ? `, then ${then}` : ""
  if (planFamily === null) {
    return `${tool} is not included in your plan right now. Open Settings > Billing${after}.`
  }
  return `${lockedToolLine(planFamily, tool)}. ${lockedToolNextStep(planFamily)}${after}.`
}

/**
 * Which plans an organization may buy. A firm on a firm plan, or still on a
 * trial, buys the firm plan: new firms see client pricing only. A firm that
 * already pays for a page plan is grandfathered and keeps changing within the
 * page plans. Everyone else buys page plans.
 */
export function purchasablePlanFamily(input: {
  effectivePlanId: string
  status: SubscriptionStatus
  isFirm: boolean
}): PlanFamily {
  if (isFirmPlanId(input.effectivePlanId)) return "firm"
  if (input.isFirm && input.status === "trialing") return "firm"
  return "page"
}

export type ClientActivationVerdict =
  | { allowed: true; reason: null }
  | { allowed: false; reason: "client_limit_reached" }

/** Error code the API returns (with HTTP 402) when the free tier refuses a
 * new active client. The web app keys its upsell on it. */
export const clientLimitReachedErrorCode = "client_limit_reached" as const

/**
 * May the firm take an action that makes this client active this month?
 *
 * Only the free tier has a limit. A client that is already active this month
 * is always allowed, so nothing a client already started is stopped halfway
 * through the month. A new client (`workspaceId` null, or not in the list) is
 * refused once the free tier's clients are all used.
 */
export function evaluateClientActivation(input: {
  planId: string
  status: SubscriptionStatus
  activeClientIds: readonly string[]
  workspaceId: string | null
}): ClientActivationVerdict {
  if (input.planId !== "FIRM_FREE") {
    return { allowed: true, reason: null }
  }

  if (input.workspaceId && input.activeClientIds.includes(input.workspaceId)) {
    return { allowed: true, reason: null }
  }

  if (input.activeClientIds.length < firmPricing.freeActiveClients) {
    return { allowed: true, reason: null }
  }

  return { allowed: false, reason: clientLimitReachedErrorCode }
}

/** `yyyy-mm` of the billing month that starts at `windowStart`, in UTC.
 * Monthly windows anchored on one day start in consecutive calendar months,
 * so the start month names the window without ambiguity. */
export function billingMonthKey(windowStart: Date): string {
  return windowStart.toISOString().slice(0, 7)
}

/**
 * Stripe meter event identifier for one active client in one billing month.
 * Reported once, the moment the client first becomes active. The API also
 * claims the same key in the database before it reports, because Stripe only
 * de-duplicates identifiers for about a day.
 */
export function activeClientMeterIdentifier(input: {
  organizationId: string
  workspaceId: string
  windowStart: Date
}): string {
  return `active:${input.organizationId}:${input.workspaceId}:${billingMonthKey(input.windowStart)}`
}

/** Stripe meter event identifier for one page-overage correction. */
export function pageOverageMeterIdentifier(input: {
  organizationId: string
  windowStart: Date
  sequence: number
}): string {
  return `overage:${input.organizationId}:${billingMonthKey(input.windowStart)}:${input.sequence}`
}

/**
 * The page-overage meter receives corrections, not raw pages: each report is
 * the difference between the overage the firm has now and the overage already
 * reported for the month. The value goes down when a new client joins the
 * pool, which Stripe allows (the cycle total floors at zero on the invoice).
 * The sum of reports is therefore always the month's true overage, whatever
 * order pages and clients arrived in.
 */
export function pageOverageCorrection(input: {
  overagePagesNow: number
  overagePagesReported: number
}): number {
  return Math.max(0, input.overagePagesNow) - input.overagePagesReported
}

/** Display copy for the firm plans, for the web app and the pricing page.
 * Features name only what code enforces; see the comment on `planCopy` in
 * billing.ts. */
export const firmPlanCopy = {
  FIRM_FREE: {
    name: "Free",
    description: "For firms starting with a few clients.",
  },
  FIRM: {
    name: "Firm",
    description: "For firms with more than 3 clients.",
  },
} as const satisfies Record<FirmPlanId, { name: string; description: string }>
